import { COHORT_OPEN_SOURCES } from './careModelAnalyticsConstants';
import CoreAnalytics from './coreAnalytics';

/** @typedef {import('./analyticsTypes').CohortCriterion} CohortCriterion */
/** @typedef {import('./analyticsTypes').CohortCriterionEventParams} CohortCriterionEventParams */

class CareModelAnalytics extends CoreAnalytics {
  EVENT_NAMES = {
    OVERVIEW_VISITED: 'care_model_overview_visited',
    VIEW_MODE_CHANGED: 'care_model_view_mode_changed',
    COHORT_METRIC_CLICKED: 'cohort_metric_clicked',
    COHORT_OPENED: 'cohort_opened',
    METRIC_VIEWED: 'metric_viewed',
    REPORTING_PERIOD_CHANGED: 'reporting_period_changed',
    AUDIT_ACTION_INITIATED: 'ai_audit_action_initiated',
    AUDIT_ACTION_FEEDBACK_SUBMITTED: 'ai_audit_action_feedback_submitted',
    CHECKOUT_AUDIT_RUN: 'ai_audit_checkout_run',
    CHECKOUT_AUDIT_NOTE_SIGNED: 'ai_audit_checkout_note_signed',
    NOTE_TEMPLATE_AUDITOR_TOGGLED: 'ai_audit_note_template_toggled',
    LAUNCH_BANNER_VIEWED: 'ai_audit_launch_banner_viewed',
    LAUNCH_BANNER_CTA_CLICKED: 'ai_audit_launch_banner_cta_clicked',
    LAUNCH_BANNER_DISMISSED: 'ai_audit_launch_banner_dismissed',
    COHORT_CREATION_STARTED: 'cohort_creation_started',
    COHORT_EDIT_OPENED: 'cohort_edit_opened',
    COHORT_CRITERION_ADDED: 'cohort_criterion_added',
    COHORT_CRITERION_REMOVED: 'cohort_criterion_removed',
    COHORT_SAVED: 'cohort_saved',
    COHORT_DELETED: 'cohort_deleted',
    COHORT_SUGGESTED_UPDATES_SHOWN: 'cohort_suggested_updates_shown',
    COHORT_SUGGESTED_UPDATES_CANCELLED: 'cohort_suggested_updates_cancelled',
    COHORT_SUGGESTED_UPDATES_CONFIRMED: 'cohort_suggested_updates_confirmed',
    COHORT_AUTO_ASSIGN_BANNER_SHOWN: 'cohort_auto_assign_banner_shown',
    COHORT_AUTO_ASSIGN_BANNER_CLICKED: 'cohort_auto_assign_banner_clicked',
    COHORT_AUTO_ASSIGN_STEP_ONE_VIEWED: 'cohort_auto_assign_step_one_viewed',
    COHORT_AUTO_ASSIGN_STEP_ONE_CONFIRMED: 'cohort_auto_assign_step_one_confirmed',
    COHORT_AUTO_ASSIGN_STEP_TWO_VIEWED: 'cohort_auto_assign_step_two_viewed',
    COHORT_AUTO_ASSIGN_STEP_TWO_COMPLETED: 'cohort_auto_assign_step_two_completed',
    COHORT_AUTO_ASSIGN_CANCELLED: 'cohort_auto_assign_cancelled',
    CARE_MODEL_GROUP_BY_CHANGED: 'care_model_group_by_changed',
    CARE_MODEL_SECOND_GROUP_BY_CHANGED: 'care_model_second_group_by_changed',
    CARE_MODEL_COLUMNS_CHANGED: 'care_model_columns_changed',
    CARE_MODEL_FILTER_APPLIED: 'care_model_filter_applied',
    CARE_MODEL_FILTER_REMOVED: 'care_model_filter_removed',
    CARE_MODEL_EXPORTED: 'care_model_exported',
    CARE_MODEL_MANAGE_COHORTS_CLICKED: 'care_model_manage_cohorts_clicked',
    CARE_MODEL_VIEW_SAVED: 'care_model_view_saved',
    CARE_MODEL_VIEW_APPLIED: 'care_model_view_applied',
  };

  METRIC_ANALYTICS_NAMES = {
    skillAcquisitionRate: 'skill_acquisition_rate',
    learningOpportunitiesRate: 'learning_opportunities_rate',
    treatmentIntensity: 'treatment_intensity',
    avgTrialsToMastery: 'avg_trials_to_mastery',
    totalMasteredTargets: 'total_mastered_targets',
  };

  COLUMN_ANALYTICS_NAMES = {
    clientsTotal: COHORT_OPEN_SOURCES.CLIENTS_TOTAL,
    clientsServed: COHORT_OPEN_SOURCES.CLIENTS_SERVED,
  };

  REPORTING_PERIOD_ANALYTICS_PRESETS = {
    last7Days: '7d',
    last30Days: '30d',
    custom: 'custom',
  };

  COHORT_SAVE_MODES = {
    CREATE: 'create',
    EDIT: 'edit',
  };

  CRITERION_FILTER_TYPE_ANALYTICS_NAMES = {
    StringInArray: 'string_in_array',
    ArrayInArray: 'array_in_array',
    StringMatch: 'string_match',
    Number: 'number',
    Duration: 'duration',
    Date: 'date',
    Client: 'client',
    User: 'user',
    ArrayContainsUser: 'array_contains_user',
    NullValue: 'null_value',
    Boolean: 'boolean',
  };

  /** The filter UI leaves the criteria type empty for these types instead of naming the operator. */
  CRITERION_DEFAULT_OPERATORS = {
    StringMatch: 'contains',
    Number: 'equals',
    Duration: 'equals',
  };

  CRITERION_DATE_FILTER_TYPE = 'Date';

  CRITERION_DATE_LIST_OPERATOR = 'custom-dates';

  UNASSIGNED_COHORT_ID = 'unassigned';

  CLIENT_DATA_COLUMN_GROUP = 'clientData';

  CUSTOM_FIELD_ANALYTICS_PREFIX = 'customData';

  /**
   * Describe the field a filter key points at. Owned here rather than by either caller, so a cohort
   * criterion and a Care Model report filter on the same field describe it identically.
   *
   * @param {string} filterKey
   * @param {Array<Object>} columns - Client columns the filter vocabulary is built from.
   * @returns {{ field: string, fieldLabel: string|null, isCustomField: boolean, filterType: string|null }}
   */
  describeCriterionField(filterKey, columns = []) {
    const column = columns.find((item) => {
      const columnFilterKey = item.filterObject
        ? `${item.filterObject}.${item.name}`
        : item.name;

      return columnFilterKey === filterKey;
    });

    const isCustomField = column?.group === this.CLIENT_DATA_COLUMN_GROUP;

    return {
      field: isCustomField ? `${this.CUSTOM_FIELD_ANALYTICS_PREFIX}.${filterKey}` : filterKey,
      fieldLabel: isCustomField ? (column?.label ?? null) : null,
      isCustomField,
      filterType: column?.filterType ?? null,
    };
  }

  /**
   * @param {string} metricId - Domain metric id (e.g. 'skillAcquisitionRate').
   * @returns {string}
   */
  getMetricAnalyticsName(metricId) {
    return this.METRIC_ANALYTICS_NAMES[metricId] || metricId;
  }

  /**
   * @param {string} columnName - Table column name (e.g. 'clientsTotal').
   * @returns {string}
   */
  getColumnAnalyticsName(columnName) {
    return this.COLUMN_ANALYTICS_NAMES[columnName] ?? this.getMetricAnalyticsName(columnName);
  }

  /**
   * @param {string|null} cohortId
   * @returns {string}
   */
  getCohortAnalyticsId(cohortId) {
    return cohortId || this.UNASSIGNED_COHORT_ID;
  }

  /**
   * @param {string|null} filterType - Filter property type of the criterion field (e.g. Number).
   * @returns {string|null}
   */
  getCriterionFilterTypeAnalyticsName(filterType) {
    return this.CRITERION_FILTER_TYPE_ANALYTICS_NAMES[filterType] || filterType || null;
  }

  /**
   * @param {string|null} filterType - Filter property type of the criterion field.
   * @param {string|null} filterCriteriaType - Operator picked in the filter UI, empty for the default one.
   * @returns {string|null} Operator name, or null for criteria that have no operator.
   */
  getCriterionOperator(filterType, filterCriteriaType = null) {
    return filterCriteriaType || this.CRITERION_DEFAULT_OPERATORS[filterType] || null;
  }

  /**
   * Turn firestore timestamps into ISO strings, at any depth of the picked value.
   * @param {*} value
   * @returns {*}
   */
  normalizeCriterionValue(value) {
    if (value === null || value === undefined) {
      return null;
    }

    if (typeof value.toDate === 'function') {
      return value.toDate().toISOString();
    }

    if (Array.isArray(value)) {
      return value.map((item) => this.normalizeCriterionValue(item));
    }

    if (typeof value === 'object') {
      return Object.entries(value).reduce((acc, [key, item]) => {
        acc[key] = this.normalizeCriterionValue(item);
        return acc;
      }, {});
    }

    return value;
  }

  /**
   * @param {string|null} filterType - Filter property type of the criterion field.
   * @param {Object} statement - Filter statement holding the values the user picked.
   * @returns {*}
   */
  getCriterionValue(filterType, statement = {}) {
    if (filterType !== this.CRITERION_DATE_FILTER_TYPE) {
      return this.normalizeCriterionValue(statement.filteredItems ?? null);
    }

    if (statement.filterCriteriaType === this.CRITERION_DATE_LIST_OPERATOR) {
      return this.normalizeCriterionValue(statement.filterDateItems ?? null);
    }

    return this.normalizeCriterionValue({
      from: statement.fromDate ?? null,
      to: statement.toDate ?? null,
    });
  }

  /**
   * Flatten a cohort criterion into event properties.
   * @param {CohortCriterion} criterion
   * @returns {Object}
   */
  getCriterionAnalyticsProps({
    field,
    fieldLabel = null,
    isCustomField = false,
    filterType = null,
    statement = {},
  }) {
    const criterionStatement = statement || {};
    const operator = this.getCriterionOperator(filterType, criterionStatement.filterCriteriaType);
    const value = this.getCriterionValue(filterType, criterionStatement);

    return {
      field,
      fieldLabel,
      isCustomField,
      filterType: this.getCriterionFilterTypeAnalyticsName(filterType),
      operator,
      isNegative: !!criterionStatement.filterNegative,
      value,
      valueCount: Array.isArray(value) ? value.length : null,
      criterionKey: `${field}:${operator || 'any'}`,
      criterionLabel: criterionStatement.label || null,
    };
  }

  /**
   * Track a visit of the Care Model overview (landing) page.
   * @param {number|null} totalCohortsShown - Number of cohort cards shown, or null when the report failed to load.
   * @returns {void}
   */
  trackOverviewVisited(totalCohortsShown = null) {
    this.track(this.EVENT_NAMES.OVERVIEW_VISITED, {
      totalCohortsShown,
      eventVersion: 2,
    });
  }

  /**
   * Track a switch between the tile and table view of the overview report.
   * @param {string} viewMode - View switched to ('tiles'|'table').
   * @param {number|null} totalCohortsShown - Number of groups the report holds at switch time.
   * @returns {void}
   */
  trackViewModeChanged(viewMode, totalCohortsShown = null) {
    if (!viewMode) {
      return;
    }

    this.track(this.EVENT_NAMES.VIEW_MODE_CHANGED, {
      viewMode,
      totalCohortsShown,
      eventVersion: 1,
    });
  }

  /**
   * Track a click on a cohort's metric on the overview page — a tile in the tile view, a cell in
   * the table view. A click carrying no metric is trackCohortOpened instead.
   * @param {Object} params
   * @param {string} params.metricId - Domain metric id (e.g. 'skillAcquisitionRate').
   * @param {string|null} params.cohortId - Cohort id, or null for the unassigned segment.
   * @param {string|null} params.cohortDimension - Dimension(s) the cohort is defined on.
   * @param {number|null} params.cohortPosition - 1-based rank of the cohort on the page at click time.
   *   Report order in the tile view, order after sorting in the table view — read it with viewMode.
   * @param {number|null} params.totalCohortsShown - Number of cohorts shown on the page.
   * @param {string|null} params.viewMode - View the click came from ('tiles'|'table').
   * @returns {void}
   */
  trackCohortMetricClicked({
    metricId,
    cohortId = null,
    cohortDimension = null,
    cohortPosition = null,
    totalCohortsShown = null,
    viewMode = null,
  }) {
    if (!metricId) {
      return;
    }

    this.track(this.EVENT_NAMES.COHORT_METRIC_CLICKED, {
      metric: this.getMetricAnalyticsName(metricId),
      cohortId: this.getCohortAnalyticsId(cohortId),
      cohortDimension,
      cohortPosition,
      totalCohortsShown,
      viewMode,
      eventVersion: 3,
    });
  }

  /**
   * Track opening a cohort from anything but a metric — its name or either clients' column. Carries
   * the payload of trackCohortMetricClicked minus the metric, so the two union into one funnel step.
   * @param {Object} params
   * @param {string|null} params.cohortId - Cohort id, or null for the unassigned segment.
   * @param {string|null} params.cohortDimension - Dimension(s) the cohort is defined on.
   * @param {number|null} params.cohortPosition - 1-based rank of the cohort on the page at click time.
   * @param {number|null} params.totalCohortsShown - Number of cohorts shown on the page.
   * @param {string|null} params.viewMode - View the click came from ('tiles'|'table').
   * @param {string} params.openedVia - Column the cohort was opened from, from COHORT_OPEN_SOURCES.
   * @returns {void}
   */
  trackCohortOpened({
    cohortId = null,
    cohortDimension = null,
    cohortPosition = null,
    totalCohortsShown = null,
    viewMode = null,
    openedVia,
  }) {
    if (!openedVia || openedVia === COHORT_OPEN_SOURCES.METRIC) {
      return;
    }

    this.track(this.EVENT_NAMES.COHORT_OPENED, {
      cohortId: this.getCohortAnalyticsId(cohortId),
      cohortDimension,
      cohortPosition,
      totalCohortsShown,
      viewMode,
      openedVia,
      eventVersion: 1,
    });
  }

  /**
   * Track a metric view on a cohort details page (initial landing or switching metrics).
   * @param {string} metricId - Domain metric id (e.g. 'skillAcquisitionRate').
   * @param {string|null} cohortId - Cohort id, or null for the unassigned segment.
   * @returns {void}
   */
  trackMetricViewed(metricId, cohortId = null) {
    if (!metricId) {
      return;
    }

    this.track(this.EVENT_NAMES.METRIC_VIEWED, {
      metric: this.getMetricAnalyticsName(metricId),
      cohortId: this.getCohortAnalyticsId(cohortId),
      eventVersion: 1,
    });
  }

  /**
   * Track a reporting period change from the date-picker (applies globally).
   * @param {string} preset - Domain preset id (e.g. 'last7Days'|'last30Days'|'custom').
   * @param {number|null} customRangeDays - Inclusive length of the custom range in days, only for the custom preset.
   * @returns {void}
   */
  trackReportingPeriodChanged(preset, customRangeDays = null) {
    if (!preset) {
      return;
    }

    this.track(this.EVENT_NAMES.REPORTING_PERIOD_CHANGED, {
      preset: this.REPORTING_PERIOD_ANALYTICS_PRESETS[preset] || preset,
      customRangeDays,
      eventVersion: 1,
    });
  }

  /**
   * @param {string} auditActionType - The type of audit action performed (e.g. 'bulk'|'proactive').
   * @param {string} sessionActivityId - The ID of the session activity.
   * @param {Array<
   *   'missingInformation'|
   *   'internalInconsistencies'|
   *   'payorBillingRisk'|
   *   'dataNarrativeMismatch'|
   *   'locationModalityInconsistencies'
   * >} focusAreas - The focus areas selected for the audit.
   * @param {'strict'|'balanced'|'lenient'} strictness - The strictness level used for the audit.
   * @param {boolean} isCompleted - Whether the audit action was completed successfully.
   * @param {string|null} verdict - The audit verdict, if available.
  * @returns {void}
   */
  trackAuditActionInitiated(
    auditActionType,
    sessionActivityId,
    focusAreas,
    strictness,
    isCompleted = false,
    verdict = '',
  ) {
    if (!auditActionType || !sessionActivityId) {
      return;
    }

    this.track(this.EVENT_NAMES.AUDIT_ACTION_INITIATED, {
      auditActionType,
      sessionActivityId,
      focusAreas,
      strictness,
      isCompleted,
      verdict,
      eventVersion: 2,
    });
  }

  /**
   * @param {Object} params
   * @param {string} params.sessionNoteTemplateId
   * @param {boolean} params.enabled - The value this save set.
   * @param {number} params.templatesWithAuditor
   * @param {number} params.templatesTotal
   */
  trackNoteTemplateAuditorToggled({
    sessionNoteTemplateId,
    enabled,
    templatesWithAuditor,
    templatesTotal,
  }) {
    if (!sessionNoteTemplateId) {
      return;
    }

    this.track(this.EVENT_NAMES.NOTE_TEMPLATE_AUDITOR_TOGGLED, {
      sessionNoteTemplateId,
      enabled,
      templatesWithAuditor,
      templatesTotal,
      eventVersion: 1,
    });
  }

  /**
   * One run of the auditor on the summary step. Carries no note text or field values.
   *
   * @param {Object} params
   * @param {string} params.sessionActivityId
   * @param {number|null} params.runNumber - Runs on this note across every client, not just this one.
   * @param {boolean} params.isRerun
   * @param {string|null} params.verdict - 'pass'|'flag'|'fail', or null when the run produced nothing.
   * @param {string|null} params.failureReason - 'unavailable'|'error', or null when the run succeeded.
   * @param {string|null} params.sessionActivityAuditId - The stored result, so the full output can be read back.
   * @param {string|null} params.notesHash - Identifies the note version this result describes.
   * @param {Object|null} params.flagResolution - How this run's flags compare with the run it
   *   follows, by focus area. Null on a first run, which has nothing to compare against. Flattened
   *   onto the event as `flags*` because PostHog filters and breaks down on scalars, cannot count an
   *   array, and lists property names across every event.
   */
  trackCheckoutAuditRun({
    sessionActivityId,
    runNumber = null,
    isRerun,
    verdict = null,
    failureReason = null,
    sessionActivityAuditId = null,
    notesHash = null,
    flagResolution = null,
  }) {
    if (!sessionActivityId) {
      return;
    }

    this.track(this.EVENT_NAMES.CHECKOUT_AUDIT_RUN, {
      sessionActivityId,
      runNumber,
      isRerun,
      verdict,
      failureReason,
      sessionActivityAuditId,
      notesHash,
      isCompleted: !!verdict,
      flagsResolvedAreas: flagResolution?.resolvedAreas ?? null,
      flagsPersistedAreas: flagResolution?.persistedAreas ?? null,
      flagsNewAreas: flagResolution?.newAreas ?? null,
      flagsResolvedCount: flagResolution?.resolvedCount ?? null,
      flagsPersistedCount: flagResolution?.persistedCount ?? null,
      flagsNewCount: flagResolution?.newCount ?? null,
      flagsUnlabelledBefore: flagResolution?.unlabelledBefore ?? null,
      flagsUnlabelledAfter: flagResolution?.unlabelledAfter ?? null,
      eventVersion: 2,
    });
  }

  /**
   * The auditor's state at the moment a note is signed. Editing and signing without asking for
   * another review emits no run event, so only this one records it.
   *
   * @param {Object} params
   * @param {string} params.sessionActivityId
   * @param {string} params.auditStatus - The state the surface was showing, e.g. 'flagged'|'outdated'.
   * @param {boolean} params.wasEditedSinceAudit
   * @param {number|null} params.runNumber - Runs on this note by the time it was signed.
   */
  trackCheckoutAuditNoteSigned({ sessionActivityId, auditStatus, wasEditedSinceAudit, runNumber = null }) {
    if (!sessionActivityId || !auditStatus) {
      return;
    }

    this.track(this.EVENT_NAMES.CHECKOUT_AUDIT_NOTE_SIGNED, {
      sessionActivityId,
      auditStatus,
      wasEditedSinceAudit,
      runNumber,
      eventVersion: 1,
    });
  }

  /**
   * Track when a user submits feedback on an AI audit action.
   * @param {string} sessionActivityId - The ID of the session activity.
   * @param {string} originalVerdict - The original audit verdict before feedback.
   * @param {string} stance - The user's stance on the audit verdict (e.g. 'accurate' or 'inaccurate').
   * @param {string|null} correctedVerdict - The user's corrected verdict, if they marked the original as inaccurate.
   * @returns {void}
   */
  trackAuditActionFeedbackSubmitted(sessionActivityId, originalVerdict, stance, correctedVerdict = null) {
    if (!sessionActivityId || !originalVerdict || !stance) {
      return;
    }

    this.track(
      this.EVENT_NAMES.AUDIT_ACTION_FEEDBACK_SUBMITTED,
      {
        sessionActivityId,
        originalVerdict,
        stance,
        correctedVerdict,
        eventVersion: 1,
      },
    );
  }

  /**
   * Track a click on "Add cohort" on the cohorts settings page. Paired with the save event through
   * cohortEditAttemptId it reads how often creation is abandoned mid-way.
   * @param {Object} params
   * @param {string} params.cohortEditAttemptId - ID of a single open-to-close pass over the cohort dialog.
   * @param {number|null} params.totalCohorts - Number of cohorts the clinic has at click time.
   * @returns {void}
   */
  trackCohortCreationStarted({ cohortEditAttemptId, totalCohorts = null }) {
    if (!cohortEditAttemptId) {
      return;
    }

    this.track(this.EVENT_NAMES.COHORT_CREATION_STARTED, {
      cohortEditAttemptId,
      totalCohorts,
      eventVersion: 1,
    });
  }

  /**
   * Track opening an existing cohort for editing.
   * @param {Object} params
   * @param {string} params.cohortEditAttemptId
   * @param {string} params.cohortId - ID of the cohort being opened.
   * @param {number|null} params.criteriaCount - Number of criteria on the cohort at open time.
   * @param {number|null} params.cohortPosition - 1-based rank of the cohort row on the page.
   * @param {number|null} params.totalCohorts - Number of cohorts the clinic has at open time.
   * @returns {void}
   */
  trackCohortEditOpened({
    cohortEditAttemptId,
    cohortId,
    criteriaCount = null,
    cohortPosition = null,
    totalCohorts = null,
  }) {
    if (!cohortEditAttemptId || !cohortId) {
      return;
    }

    this.track(this.EVENT_NAMES.COHORT_EDIT_OPENED, {
      cohortEditAttemptId,
      cohortId,
      criteriaCount,
      cohortPosition,
      totalCohorts,
      eventVersion: 1,
    });
  }

  /**
   * Track adding a criterion to a cohort (new or existing).
   * @param {CohortCriterionEventParams} params
   * @returns {void}
   */
  trackCohortCriterionAdded(params) {
    this.trackCohortCriterion(this.EVENT_NAMES.COHORT_CRITERION_ADDED, params);
  }

  /**
   * Track removing a criterion from a cohort (new or existing).
   * @param {CohortCriterionEventParams} params
   * @returns {void}
   */
  trackCohortCriterionRemoved(params) {
    this.trackCohortCriterion(this.EVENT_NAMES.COHORT_CRITERION_REMOVED, params);
  }

  /**
   * Added and removed criteria carry the same payload, so both sides of one criterion can be paired.
   * @param {string} eventName
   * @param {CohortCriterionEventParams} params
   * @returns {void}
   */
  trackCohortCriterion(eventName, {
    cohortEditAttemptId,
    cohortId = null,
    isNewCohort = false,
    isReplacement = false,
    criteriaCount = null,
    criterion = {},
  }) {
    if (!cohortEditAttemptId || !criterion.field || !criterion.statement) {
      return;
    }

    this.track(eventName, {
      cohortEditAttemptId,
      cohortId,
      isNewCohort,
      isReplacement,
      criteriaCount,
      ...this.getCriterionAnalyticsProps(criterion),
      eventVersion: 1,
    });
  }

  /**
   * Track a successful cohort save, for both creation and editing.
   * @param {Object} params
   * @param {string} params.cohortEditAttemptId
   * @param {string|null} params.cohortId - ID of the saved cohort.
   * @param {boolean} params.isCreate - Whether the save created a new cohort.
   * @param {number|null} params.criteriaCount - Number of criteria on the cohort at save time.
   * @returns {void}
   */
  trackCohortSaved({
    cohortEditAttemptId,
    cohortId = null,
    isCreate = false,
    criteriaCount = null,
  }) {
    if (!cohortEditAttemptId) {
      return;
    }

    this.track(this.EVENT_NAMES.COHORT_SAVED, {
      cohortEditAttemptId,
      cohortId,
      mode: isCreate ? this.COHORT_SAVE_MODES.CREATE : this.COHORT_SAVE_MODES.EDIT,
      criteriaCount,
      eventVersion: 1,
    });
  }

  /**
   * Track a successful cohort deletion.
   * @param {Object} params
   * @param {string} params.cohortEditAttemptId
   * @param {string} params.cohortId - ID of the deleted cohort.
   * @param {number|null} params.criteriaCount - Number of criteria the cohort had when it was deleted.
   * @param {number} params.unassignedClientsCount - Clients the deletion unassigned from the cohort.
   * @returns {void}
   */
  trackCohortDeleted({
    cohortEditAttemptId,
    cohortId,
    criteriaCount = null,
    unassignedClientsCount = 0,
  }) {
    if (!cohortEditAttemptId || !cohortId) {
      return;
    }

    this.track(this.EVENT_NAMES.COHORT_DELETED, {
      cohortEditAttemptId,
      cohortId,
      criteriaCount,
      unassignedClientsCount,
      eventVersion: 2,
    });
  }

  trackCohortSuggestedUpdatesShown(clientsCount) {
    this.track(this.EVENT_NAMES.COHORT_SUGGESTED_UPDATES_SHOWN, {
      clientsCount,
    });
  }

  trackCohortSuggestedUpdatesCancelled(ignoredClientsCount) {
    this.track(this.EVENT_NAMES.COHORT_SUGGESTED_UPDATES_CANCELLED, {
      ignoredClientsCount,
    });
  }

  trackCohortSuggestedUpdatesConfirmed(acceptedClientsCount) {
    this.track(this.EVENT_NAMES.COHORT_SUGGESTED_UPDATES_CONFIRMED, {
      acceptedClientsCount,
    });
  }

  /**
   * @param {string} variant A `LAUNCH_BANNER_VARIANTS` value: whether the clinic had used the
   *   auditor before the subscription launch.
   */
  trackAuditLaunchBannerViewed(variant) {
    this.track(this.EVENT_NAMES.LAUNCH_BANNER_VIEWED, { variant });
  }

  /**
   * @param {string} variant A `LAUNCH_BANNER_VARIANTS` value.
   * @param {'howItWorks'|'turnItOn'} cta Which of the two calls to action was taken.
   */
  trackAuditLaunchBannerCtaClicked(variant, cta) {
    this.track(this.EVENT_NAMES.LAUNCH_BANNER_CTA_CLICKED, { variant, cta });
  }

  /**
   * @param {string} variant A `LAUNCH_BANNER_VARIANTS` value.
   */
  trackAuditLaunchBannerDismissed(variant) {
    this.track(this.EVENT_NAMES.LAUNCH_BANNER_DISMISSED, { variant });
  }

  trackCohortAutoAssignBannerShown(surface, clientsCount) {
    this.track(this.EVENT_NAMES.COHORT_AUTO_ASSIGN_BANNER_SHOWN, {
      surface,
      clientsCount,
    });
  }

  trackCohortAutoAssignBannerClicked(surface, clientsCount) {
    this.track(this.EVENT_NAMES.COHORT_AUTO_ASSIGN_BANNER_CLICKED, {
      surface,
      clientsCount,
    });
  }

  trackCohortAutoAssignStepOneViewed(surface, clientsCount) {
    this.track(this.EVENT_NAMES.COHORT_AUTO_ASSIGN_STEP_ONE_VIEWED, {
      surface,
      clientsCount,
    });
  }

  trackCohortAutoAssignStepOneConfirmed(
    surface,
    acceptedCount,
    ignoredCount,
  ) {
    this.track(this.EVENT_NAMES.COHORT_AUTO_ASSIGN_STEP_ONE_CONFIRMED, {
      surface,
      acceptedCount,
      ignoredCount,
    });
  }

  trackCohortAutoAssignStepTwoViewed(
    surface,
    clientsCount,
    entryPoint,
  ) {
    this.track(this.EVENT_NAMES.COHORT_AUTO_ASSIGN_STEP_TWO_VIEWED, {
      surface,
      clientsCount,
      entryPoint,
    });
  }

  trackCohortAutoAssignStepTwoCompleted(
    surface,
    manuallyAssignedCount,
    remainingUnassignedCount,
  ) {
    this.track(this.EVENT_NAMES.COHORT_AUTO_ASSIGN_STEP_TWO_COMPLETED, {
      surface,
      manuallyAssignedCount,
      remainingUnassignedCount,
    });
  }

  trackCohortAutoAssignCancelled(surface, step) {
    this.track(this.EVENT_NAMES.COHORT_AUTO_ASSIGN_CANCELLED, {
      surface,
      step,
    });
  }

  /**
   * Track a change of the Care Model grouping dimension.
   *
   * @param {{dimension: string, key: string|null, label: string|null}} groupBy
   * @param {boolean} isCustomField - Decided by the caller; the segment ids are the report
   *   domain's, not this file's.
   * @returns {void}
   */
  trackCareModelGroupByChanged(groupBy, isCustomField = false) {
    if (!groupBy?.dimension) {
      return;
    }

    this.track(this.EVENT_NAMES.CARE_MODEL_GROUP_BY_CHANGED, {
      groupBy: groupBy.dimension,
      isCustomField,
      customFieldId: isCustomField ? (groupBy.key ?? null) : null,
      customFieldName: isCustomField ? (groupBy.label ?? null) : null,
      eventVersion: 2,
    });
  }

  /**
   * Track a change of the Care Model second grouping dimension - the nested table. Unlike the
   * level above, clearing it is reported too, as a null dimension: dropping a second level is as
   * much of an adoption signal as picking one.
   *
   * @param {Object} params
   * @param {{dimension: string, key: string|null, label: string|null}|null} params.subGroupBy - Null when cleared.
   * @param {boolean} params.isSubGroupByCustomField - Decided by the caller; the segment ids are
   *   the report domain's, not this file's.
   * @param {boolean} params.switchedToTable - Whether picking it moved the report out of Tiles.
   * @param {{dimension: string, key: string|null, label: string|null}|null} params.groupBy - The
   *   first-level grouping in effect, so this event alone describes the combined grouping.
   * @param {boolean} params.isGroupByCustomField - Same as isSubGroupByCustomField, for params.groupBy.
   * @returns {void}
   */
  trackCareModelSecondGroupByChanged({
    subGroupBy = null,
    isSubGroupByCustomField = false,
    switchedToTable = false,
    groupBy = null,
    isGroupByCustomField = false,
  }) {
    this.track(this.EVENT_NAMES.CARE_MODEL_SECOND_GROUP_BY_CHANGED, {
      subGroupBy: subGroupBy?.dimension ?? null,
      isSubGroupByCustomField,
      subGroupByCustomFieldId: isSubGroupByCustomField ? (subGroupBy?.key ?? null) : null,
      subGroupByCustomFieldName: isSubGroupByCustomField ? (subGroupBy?.label ?? null) : null,
      switchedToTable,
      groupBy: groupBy?.dimension ?? null,
      isGroupByCustomField,
      groupByCustomFieldId: isGroupByCustomField ? (groupBy?.key ?? null) : null,
      groupByCustomFieldName: isGroupByCustomField ? (groupBy?.label ?? null) : null,
      eventVersion: 2,
    });
  }

  /**
   * @param {Object} params
   * @param {string[]} params.visibleColumns - Column names kept, in display order.
   * @param {string[]} params.hiddenColumns - Column names dropped.
   * @param {boolean} params.isReordered - Whether the kept columns left their default order.
   * @returns {void}
   */
  trackCareModelColumnsChanged({ visibleColumns = [], hiddenColumns = [], isReordered = false }) {
    this.track(this.EVENT_NAMES.CARE_MODEL_COLUMNS_CHANGED, {
      visibleColumns: visibleColumns.map((name) => this.getColumnAnalyticsName(name)),
      hiddenColumns: hiddenColumns.map((name) => this.getColumnAnalyticsName(name)),
      visibleColumnCount: visibleColumns.length,
      hiddenColumnCount: hiddenColumns.length,
      isReordered,
      eventVersion: 1,
    });
  }

  /**
   * Track a filter applied to the Care Model report. Reported once the user applies the dialog, not
   * per value picked, so the count is of filters people actually use.
   *
   * @param {Object} params
   * @param {CohortCriterion} params.criterion - Criterion as described by describeCriterionField,
   *   plus the statement the user picked.
   * @param {number} params.activeFilterCount - Criteria active after applying this one.
   * @returns {void}
   */
  trackCareModelFilterApplied({ criterion, activeFilterCount = 0 }) {
    if (!criterion?.field) {
      return;
    }

    this.track(this.EVENT_NAMES.CARE_MODEL_FILTER_APPLIED, {
      ...this.getCriterionAnalyticsProps(criterion),
      activeFilterCount,
      eventVersion: 1,
    });
  }

  /**
   * Track a criterion being dropped, through its chip, by replacing it in the dialog, or by
   * clearing the filter from the empty-result banner.
   *
   * @param {Object} params
   * @param {CohortCriterion} params.criterion - Criterion as in trackCareModelFilterApplied.
   * @param {number} params.activeFilterCount - Criteria still active afterwards.
   * @param {'chip'|'dialog'|'banner'} params.removedVia
   * @returns {void}
   */
  trackCareModelFilterRemoved({ criterion, activeFilterCount = 0, removedVia = 'chip' }) {
    if (!criterion?.field) {
      return;
    }

    this.track(this.EVENT_NAMES.CARE_MODEL_FILTER_REMOVED, {
      ...this.getCriterionAnalyticsProps(criterion),
      activeFilterCount,
      removedVia,
      eventVersion: 1,
    });
  }

  /**
   * @param {Object} params
   * @param {string} params.format - File format written (e.g. 'xlsx').
   * @param {number} params.rowCount - Rows in the file, both grouping levels counted.
   * @param {number} params.columnCount - Columns in the file, including the derived change columns.
   * @param {number} params.clientsTotal - Clients summed from the file's top-level segments. Not a
   *   metric, by product's own call - every Care Model report carries it regardless of what the
   *   reader chose to show.
   * @param {number} params.clientsServed - Same as clientsTotal, for the served count.
   * @param {{dimension: string, key: string|null, label: string|null}|null} params.groupBy
   * @param {boolean} params.isGroupByCustomField - Decided by the caller; the segment ids are the
   *   report domain's, not this file's.
   * @param {{dimension: string, key: string|null, label: string|null}|null} params.subGroupBy - Null
   *   when the export has no second level.
   * @param {boolean} params.isSubGroupByCustomField - Same as isGroupByCustomField, for params.subGroupBy.
   * @param {boolean} params.hasActiveFilters - Whether the exported view was filtered.
   * @param {string[]} params.filteredFields - Field identifiers of the active filters, no values.
   * @param {string[]} params.filteredCohortIds - Cohort ids the view was filtered to, via the Cohort
   *   filter. Not client data - a cohort is a category the clinic itself defined.
   * @param {string[]} params.filteredCohortNames - Names for filteredCohortIds, same order.
   * @param {string[]} params.cohortIds - Cohort ids actually in the file, when cohort is the
   *   grouping dimension at whichever level carries one.
   * @param {string[]} params.cohortNames - Names for cohortIds, same order.
   * @param {string[]} params.metricsIncluded - Analytics names of the metric columns in the file.
   * @param {string|null} params.reportingPeriodStart - ISO date, start of the exported period.
   * @param {string|null} params.reportingPeriodEnd - ISO date, end of the exported period.
   * @param {'savedView'|'adHoc'|null} params.viewSource - Whether the export still matches a saved
   *   view as applied/saved, or the user has since changed something by hand.
   * @returns {void}
   */
  trackCareModelExported({
    format,
    rowCount = 0,
    columnCount = 0,
    clientsTotal = 0,
    clientsServed = 0,
    groupBy = null,
    isGroupByCustomField = false,
    subGroupBy = null,
    isSubGroupByCustomField = false,
    hasActiveFilters = false,
    filteredFields = [],
    filteredCohortIds = [],
    filteredCohortNames = [],
    cohortIds = [],
    cohortNames = [],
    metricsIncluded = [],
    reportingPeriodStart = null,
    reportingPeriodEnd = null,
    viewSource = null,
  }) {
    if (!format) {
      return;
    }

    this.track(this.EVENT_NAMES.CARE_MODEL_EXPORTED, {
      format,
      rowCount,
      columnCount,
      clientsTotal,
      clientsServed,
      groupBy: groupBy?.dimension ?? null,
      isGroupByCustomField,
      groupByCustomFieldId: isGroupByCustomField ? (groupBy?.key ?? null) : null,
      groupByCustomFieldName: isGroupByCustomField ? (groupBy?.label ?? null) : null,
      subGroupBy: subGroupBy?.dimension ?? null,
      isSubGroupByCustomField,
      subGroupByCustomFieldId: isSubGroupByCustomField ? (subGroupBy?.key ?? null) : null,
      subGroupByCustomFieldName: isSubGroupByCustomField ? (subGroupBy?.label ?? null) : null,
      hasActiveFilters,
      filteredFields,
      filteredFieldCount: filteredFields.length,
      filteredCohortIds,
      filteredCohortNames,
      cohortIds,
      cohortNames,
      metricsIncluded,
      metricsIncludedCount: metricsIncluded.length,
      reportingPeriodStart,
      reportingPeriodEnd,
      viewSource,
      eventVersion: 3,
    });
  }

  /**
   * Track the "Manage cohorts" link in the Care Model grouping menu. Carries the grouping in effect
   * at click time, so it reads whether people reach cohort settings from cohort grouping or from
   * some other grouping.
   *
   * @param {{dimension: string, key: string|null}|null} groupBy - Grouping selected when clicked.
   * @returns {void}
   */
  trackCareModelManageCohortsClicked(groupBy = null) {
    this.track(this.EVENT_NAMES.CARE_MODEL_MANAGE_COHORTS_CLICKED, {
      groupBy: groupBy?.dimension ?? null,
    });
  }

  /**
   * Track a named view of the segmented report being saved. The properties describe the shape of
   * the configuration, never the name: that is the user's own wording about their clinic.
   *
   * @param {Object} params
   * @param {string} params.viewMode - Tiles or table, as saved.
   * @param {boolean} params.hasSecondGrouping
   * @param {number} params.visibleColumnCount
   * @param {number} params.activeFilterCount
   * @param {boolean} params.isNew - Whether a view was created or one the user had was updated.
   * @returns {void}
   */
  trackCareModelViewSaved({
    viewMode,
    hasSecondGrouping = false,
    visibleColumnCount = 0,
    activeFilterCount = 0,
    isNew = false,
  }) {
    this.track(this.EVENT_NAMES.CARE_MODEL_VIEW_SAVED, {
      viewMode,
      hasSecondGrouping,
      visibleColumnCount,
      activeFilterCount,
      isNew,
      eventVersion: 1,
    });
  }

  /**
   * Track a saved view being put on screen. Reported after it is applied, so the properties are
   * what the reader ended up looking at.
   *
   * @param {Object} params
   * @param {string} params.viewMode
   * @param {boolean} params.hasSecondGrouping
   * @param {number} params.visibleColumnCount
   * @param {number} params.activeFilterCount
   * @returns {void}
   */
  trackCareModelViewApplied({
    viewMode,
    hasSecondGrouping = false,
    visibleColumnCount = 0,
    activeFilterCount = 0,
  }) {
    this.track(this.EVENT_NAMES.CARE_MODEL_VIEW_APPLIED, {
      viewMode,
      hasSecondGrouping,
      visibleColumnCount,
      activeFilterCount,
      eventVersion: 1,
    });
  }
}

export default new CareModelAnalytics();
